import type { SvgIconComponent } from '@mui/icons-material';
import type { BlockCategory } from './types';
import { BLOCK_DEFINITIONS, definitionKey, insertableBlocks, TEXT_LIKE_NODE_TYPES, type BlockDefinition } from './blockRegistry';

/** One entry of the slash menu / block library, with translated texts. */
export interface BlockMenuItem {
    key: string;
    label: string;
    description: string;
    icon: SvgIconComponent;
    category: BlockCategory;
    nodeType: string;
    attrs?: Record<string, unknown>;
    aliases: string[];
}

export type Translate = (key: string, options?: Record<string, unknown>) => string;

export function toMenuItem(def: BlockDefinition, translate: Translate): BlockMenuItem {
    const key = definitionKey(def);
    return {
        key: `${def.nodeType}:${JSON.stringify(def.attrs ?? {})}`,
        label: translate(`blockEditor.types.${key}.label`),
        description: translate(`blockEditor.types.${key}.description`),
        icon: def.icon,
        category: def.category,
        nodeType: def.nodeType,
        attrs: def.attrs,
        aliases: def.aliases,
    };
}

/** Insertable blocks as menu items (slash menu, library panel). Nested editors get no layout blocks. */
export function getBlockMenuItems(translate: Translate, options: { nested?: boolean } = {}, defs: BlockDefinition[] = insertableBlocks()): BlockMenuItem[] {
    return defs.filter((d) => !options.nested || d.category !== 'layout').map((d) => toMenuItem(d, translate));
}

/** Matches label, description and aliases, case-insensitive. */
export function filterMenuItems(items: BlockMenuItem[], query: string): BlockMenuItem[] {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(
        (item) =>
            item.label.toLowerCase().includes(q) ||
            item.description.toLowerCase().includes(q) ||
            item.aliases.some((alias) => alias.toLowerCase().includes(q)),
    );
}

/** Conversion targets for the type switch: text-like blocks except the current one. */
export function getConversionTargets(nodeType: string, attrs: Record<string, unknown> | undefined, translate: Translate): BlockMenuItem[] {
    return BLOCK_DEFINITIONS.filter((d) => TEXT_LIKE_NODE_TYPES.has(d.nodeType) && d.implemented)
        .filter((d) => {
            if (d.nodeType !== nodeType) return true;
            if (nodeType === 'headingBlock' && d.attrs?.level !== attrs?.level) return true;
            return false;
        })
        .map((d) => toMenuItem(d, translate));
}

export function canSwitchBlockType(nodeType: string): boolean {
    return TEXT_LIKE_NODE_TYPES.has(nodeType);
}
